/**
 * ModalMessageCard — chat-bubble card used exclusively inside ConversationContextModal.
 *
 * This is the V2 design of the conversation-context card featuring XDS chatBubble tokens,
 * inline blur toggle for unsafe content, and embedded voice-memo player.
 *
 * The original `ConversationContextCard` (used by SlidingContextView) is intentionally
 * left unchanged so the flag-off path has zero visual regressions.
 */

import type { ContentCardItem } from '@content-search/domain';
import { InlineAudioPlayer } from '@core/components';
import { getContentDisplayText } from '@core/utils';
import {
  BlurToggle,
  ButtonType,
  ChatBubble,
  ChatBubbleAlignment,
  ChatBubbleVariant,
  ChevronDown,
  ChevronUp,
  IconButton,
  IconSize,
} from '@soteria/design-system-ve';
import { useState } from 'react';

import * as styles from './ModalMessageCard.css';

/** Format a timestamp in the user's local timezone */
function formatLocalTimestamp(timestamp: string | Date): string {
  try {
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;

    if (isNaN(date.getTime())) {
      return timestamp.toString() || 'Invalid date';
    }

    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZoneName: 'short',
    };

    return date.toLocaleString(undefined, options);
  } catch {
    return timestamp.toString() || 'Unknown date';
  }
}

export interface ModalMessageCardProps {
  item: ContentCardItem;
  isOriginalSelection?: boolean;
  alignment: 'left' | 'right';
  onClick?: () => void;
  mediaServiceUrl?: string;
}

/** Resolve display name and optional tooltip from subject owner */
function getDisplayInfo(subjectOwner?: { username?: string; userId?: string }): { displayName: string; tooltip?: string } {
  const { username, userId } = subjectOwner ?? {};

  if (username) return { displayName: username, tooltip: userId };
  if (userId) return { displayName: userId };

  return { displayName: 'Unknown User' };
}

/** Lookup table mapping alignment to ChatBubble variant and alignment — avoids inline ternaries */
const BUBBLE_CONFIG = {
  left: { variant: ChatBubbleVariant.MessagesOthers, alignment: ChatBubbleAlignment.Start },
  right: { variant: ChatBubbleVariant.MessagesMe, alignment: ChatBubbleAlignment.End },
} as const;

/** Build an accessible label describing the message content (truncated to 100 chars) */
function getBubbleAriaLabel(displayName: string, text?: string, displayText?: string): string {
  const content = text?.substring(0, 100) || displayText || '';
  const ellipsis = text && text.length > 100 ? '...' : '';

  return `Message from ${displayName}: ${content}${ellipsis}`;
}

/** Voice-memo audio player rendered inside a ChatBubble.Audio slot — renders nothing when no audio */
function VoiceMemoSection({
  contentUrl,
  audioFileUrl,
  audioId,
  mediaServiceUrl,
}: {
  contentUrl?: string;
  audioFileUrl?: string;
  audioId: string;
  mediaServiceUrl?: string;
}) {
  const audioUrl = contentUrl || audioFileUrl;

  if (!audioUrl) return null;

  return (
    <ChatBubble.Audio>
      <div role="presentation" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
        <InlineAudioPlayer audioId={audioId} audioUrl={audioUrl} mediaServiceUrl={mediaServiceUrl} />
      </div>
    </ChatBubble.Audio>
  );
}

/**
 * ModalMessageCard — uses the design-system ChatBubble compound component to render
 * a chat bubble with text, optional voice memo, and unsafe-content indicators.
 *
 * The outer wrapper uses `role="button"` instead of ChatBubble's `interactive` prop
 * to avoid nesting interactive elements (blur-toggle IconButton, audio player) inside
 * a native `<button>`.
 */
export function ModalMessageCard({ item, isOriginalSelection = false, alignment, onClick, mediaServiceUrl }: ModalMessageCardProps) {
  const isUnsafe = item.classificationResult === 'rejected';
  const [isContentBlurred, setIsContentBlurred] = useState(isUnsafe);
  const displayText = getContentDisplayText(item.text, item.contentUrl, item.audioFileUrl);
  const { displayName } = getDisplayInfo(item.subjectOwner);
  const { variant, alignment: bubbleAlignment } = BUBBLE_CONFIG[alignment];
  const ariaLabel = getBubbleAriaLabel(displayName, item.text, displayText);

  return (
    <div
      className={`${styles.messageWrapper} ${isOriginalSelection ? styles.originalSelectedMessage : ''}`}
      data-is-original-selection={isOriginalSelection}
      data-message-id={item.id}
      data-testid="content-card"
    >
      <div
        aria-label={ariaLabel}
        className={styles.clickableWrapper}
        role="button"
        tabIndex={0}
        onClick={() => onClick?.()}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
      >
        <ChatBubble
          arrow
          alignment={bubbleAlignment}
          isUnsafe={isUnsafe}
          timestamp={item.timestamp ? formatLocalTimestamp(item.timestamp) : undefined}
          username={displayName}
          variant={variant}
        >
          <div className={styles.textWithToggle}>
            <ChatBubble.Text className={isUnsafe && isContentBlurred ? styles.blurredText : undefined}>{displayText}</ChatBubble.Text>
            {isUnsafe && (
              <div className={styles.blurToggleContainer} role="presentation" onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
                <BlurToggle isBlurred={isContentBlurred} onToggle={() => setIsContentBlurred(prev => !prev)} />
              </div>
            )}
          </div>
          <VoiceMemoSection audioFileUrl={item.audioFileUrl} audioId={item.id} contentUrl={item.contentUrl} mediaServiceUrl={mediaServiceUrl} />
        </ChatBubble>
      </div>
    </div>
  );
}

export interface ModalLoadMoreButtonProps {
  direction: 'up' | 'down';
  onClick: () => void;
  isLoading?: boolean;
}

/**
 * LoadMoreButton component for loading additional conversation context (modal variant)
 */
export function ModalLoadMoreButton({ direction, onClick, isLoading = false }: ModalLoadMoreButtonProps) {
  return (
    <div className={styles.loadMoreContainer} data-testid={`load-more-${direction}`}>
      <IconButton
        aria-label={isLoading ? 'Loading more messages' : `Load more messages ${direction === 'up' ? 'above' : 'below'}`}
        buttonType={ButtonType.Tertiary}
        disabled={isLoading}
        icon={direction === 'up' ? ChevronUp : ChevronDown}
        iconSize={IconSize.XSmall}
        onClick={!isLoading ? onClick : undefined}
      />
    </div>
  );
}
